import { useState } from "react";
import { useAuth } from "@/contexts/auth";
import ReviewForm from "@/components/ReviewForm";
import { Button } from "@/components/ui/button";
import AdUnit from "@/components/ads/AdUnit";
import AfterIdle from "@/components/util/AfterIdle";

import { AuthUserLite } from "@/types/domain";

interface ServiceSidebarProps {
  user: AuthUserLite | null;
  serviceId: string;
  hasUserReviewed: boolean;
  onReviewSubmit: () => void;
  providerName?: string;
  whatsappNumber?: string;
  isPremiumProvider: boolean;
  adsEnabled: boolean;
}

const ServiceSidebar = ({
  user,
  serviceId,
  hasUserReviewed,
  onReviewSubmit,
  providerName = "",
  whatsappNumber = "",
  isPremiumProvider,
  adsEnabled,
}: ServiceSidebarProps) => {
  const { user: authUser } = useAuth();
  const [showReviewForm, setShowReviewForm] = useState(false);

  const handleReviewSubmit = () => {
    setShowReviewForm(false);
    onReviewSubmit();
  };

  return (
    <aside>
      <div className="sticky top-24 space-y-6">
        {/* Review Form Card - Only show if user hasn't reviewed yet */}
        {authUser && !hasUserReviewed && !showReviewForm && (
          <div className="space-y-4 rounded-md border border-brand-cloud bg-white p-5 shadow-sm">
            <p className="text-xs font-medium uppercase text-brand-slate">Reviews</p>
            <h3 className="public-card-title">Share your experience</h3>
            <p className="text-sm leading-6 text-brand-slate">
              Have you used this service? Help others by sharing your experience.
            </p>
            <Button 
              className="w-full rounded-md bg-brand-ink text-white hover:bg-brand-charcoal"
              onClick={() => setShowReviewForm(true)}
            >
              Write a Review
            </Button>
          </div>
        )}
        
        {/* Already Reviewed Message */}
        {authUser && hasUserReviewed && (
          <div className="space-y-4 rounded-md border border-brand-cloud bg-white p-5 shadow-sm">
            <p className="text-xs font-medium uppercase text-brand-slate">Reviews</p>
            <h3 className="public-card-title">Share your experience</h3>
            <p className="text-sm leading-6 text-brand-slate">
              You've already reviewed this service provider. Thank you for sharing your experience!
            </p>
          </div>
        )}
        
        {/* Show Review Form when button is clicked */}
        {showReviewForm && (
          <ReviewForm 
            serviceId={serviceId} 
            onReviewSubmit={handleReviewSubmit} 
            onCancel={() => setShowReviewForm(false)} 
          />
        )}

        {/* New Ad Unit - Only show for non-premium providers and if ads are enabled */}
        {adsEnabled && !isPremiumProvider && (
          <AfterIdle timeoutMs={1200}>
            <div className="flex justify-center overflow-hidden rounded-md border border-brand-cloud bg-white p-4">
              <AdUnit 
                adSlot="3028071677" 
                adFormat="RECTANGLE" 
                className="mx-auto w-full" 
              />
            </div>
          </AfterIdle>
        )}

        <div className="rounded-md border border-brand-cloud bg-white p-5 shadow-sm">
          <p className="text-xs font-medium uppercase text-brand-slate">For providers</p>
          <h3 className="mt-3 public-card-title">List your business</h3>
          <p className="mt-3 text-sm leading-6 text-brand-slate">
            Get in front of Hong Kong locals and expats looking for trusted services.
          </p>
          <Button
            className="mt-5 w-full rounded-md bg-brand-ink text-white hover:bg-brand-charcoal"
            onClick={() => window.location.href = '/submit-listing'}
          >
            Add Your Listing
          </Button>
        </div>
      </div>
    </aside>
  );
};

export default ServiceSidebar;
